import { ProLightIndustryWindows } from '@nexelem/icons/ProLightIndustryWindows';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { Banner } from '@nexelem/components';
import { useFormContext } from 'react-hook-form';
import { type TaskView, TaskStatus } from '@nexelem/graphne-api';
import { useCustomTranslation } from '../../../../../../hooks';
import { getRetoolingDuration } from '../../../../../../utils/functions/getDuration';
import { getTimeFormat } from '../../../../../../utils/functions/getTimeFormat';
import { QueuesSelectInput } from './QueuesSelectInput';
import { StartAndEndDateInput } from './StartAndEndDateInput';
import { DurationInput } from './DurationInput';
import type { AddToPlanFormSchema } from './getAddToPlanFormSchema';
import { startedStatuses } from '../constants';

export type AddToPlanFormProps = {
  task: TaskView;
  size: 'small' | 'big';
  isNewTask?: boolean;
};

export const AddTaskToPlanForm = ({ task, size = 'small', isNewTask = false }: AddToPlanFormProps) => {
  const { control } = useFormContext<AddToPlanFormSchema>();
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');

  const { id: taskId, start, setupEnd, duration, estimatedDurations, status } = task;
  const productionDuration =
    (duration && getTimeFormat(duration)) || getTimeFormat(estimatedDurations?.median, 'seconds');
  const retoolingDuration = getRetoolingDuration(start, setupEnd);

  const hasStarted = startedStatuses.includes(status);
  const productionDurationLabel = hasStarted ? tWrapper('productionDuration') : tWrapper('productionEstimatedDuration');
  const retoolingDurationLabel = hasStarted ? tWrapper('retoolingDuration') : tWrapper('retoolingEstimatedDuration');
  const isUnplanned = status === TaskStatus.NotPlanned;

  if (isUnplanned && !isNewTask) {
    return (
      <div className="flex flex-col gap-6">
        <DurationInput
          duration={productionDuration}
          Icon={ProLightIndustryWindows}
          title={productionDurationLabel}
          type="production"
        />
        {retoolingDuration && (
          <DurationInput
            duration={retoolingDuration}
            Icon={CUSTOM_ICONS.MachineRetool}
            title={retoolingDurationLabel}
            type="retooling"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <QueuesSelectInput taskId={taskId} />
      <StartAndEndDateInput control={control} size={size} status={status} />
      <Banner className="bg-transparent" emphasis="weak" message={tWrapper('info')} type="info" />
      <DurationInput
        isEditable
        duration={productionDuration}
        Icon={ProLightIndustryWindows}
        title={productionDurationLabel}
        type="production"
      />
      {retoolingDuration && (
        <DurationInput
          duration={retoolingDuration}
          Icon={CUSTOM_ICONS.MachineRetool}
          title={retoolingDurationLabel}
          type="retooling"
        />
      )}
    </div>
  );
};
